import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCheckCircle } = FiIcons;

export default function BrandShowcase({ brands }) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="grid gap-8 lg:grid-cols-[380px_1fr]">
      {/* Sidebar Navigation */}
      <div className="flex flex-col gap-3">
        {brands.map((brand, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`group relative flex items-center gap-4 rounded-2xl border p-4 text-left transition-all duration-300 ${
              activeTab === index
                ? 'border-blue-500/50 bg-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.15)]'
                : 'border-white/5 bg-white/5 hover:border-white/20 hover:bg-white/10'
            }`}
          >
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors ${
              activeTab === index ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/60 group-hover:text-white'
            }`}>
              <SafeIcon icon={brand.icon} className="h-6 w-6" />
            </div>
            <div>
              <div className={`font-semibold transition-colors ${activeTab === index ? 'text-white' : 'text-white/60 group-hover:text-white'}`}>
                {brand.name}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 group-hover:text-white/60">
                {brand.category}
              </div>
            </div>
            {activeTab === index && (
              <motion.div
                layoutId="active-indicator"
                className="absolute right-4 text-blue-400"
              >
                <div className="h-1.5 w-1.5 rounded-full bg-current shadow-[0_0_8px_currentColor]" />
              </motion.div>
            )}
          </button>
        ))}
      </div>

      {/* Detail Display Area */}
      <div className="relative min-h-[400px] overflow-hidden rounded-[32px] border border-white/10 bg-white/5 p-8 backdrop-blur-sm md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex h-full flex-col"
          >
            <div className="flex items-center gap-4 mb-6">
               <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-blue-400 border border-blue-500/20">
                <SafeIcon icon={brands[activeTab].icon} className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-white md:text-4xl">{brands[activeTab].name}</h3>
                <p className="text-blue-400 font-medium tracking-wide uppercase text-xs mt-1">{brands[activeTab].tagline}</p>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-white/70 mb-8 max-w-2xl">
              {brands[activeTab].longDesc}
            </p>

            <div className="grid gap-4 sm:grid-cols-2 mb-10">
              {brands[activeTab].features.map((feature, i) => {
                const isObject = typeof feature === 'object';
                const text = isObject ? feature.text : feature;
                const url = isObject ? feature.url : null;
                
                return (
                  <div key={i} className="flex items-start gap-3 text-white/80">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-blue-500 mt-0.5" />
                    {url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-blue-400 underline decoration-blue-500/30 underline-offset-4 transition-colors">
                        {text}
                      </a>
                    ) : (
                      <span className="text-sm">{text}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-auto flex flex-wrap gap-4 pt-6 border-t border-white/10">
              {brands[activeTab].ctas ? brands[activeTab].ctas.map((cta, idx) => {
                const isPrimary = cta.primary;
                const baseClasses = "inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition";
                
                let classes = "";
                if (cta.className) {
                  classes = `${baseClasses} ${cta.className}`;
                } else if (isPrimary) {
                  classes = `${baseClasses} bg-white text-[#060b19] hover:bg-blue-50`;
                } else {
                  classes = `${baseClasses} border border-white/10 bg-white/5 text-white hover:bg-white/10`;
                }

                if (cta.url) {
                  return (
                    <a 
                      key={idx}
                      href={cta.url}
                      target={cta.url.startsWith('http') ? "_blank" : "_self"}
                      rel={cta.url.startsWith('http') ? "noopener noreferrer" : ""}
                      className={classes}
                    >
                      {cta.text} {isPrimary && <SafeIcon icon={FiArrowRight} className="h-4 w-4" />}
                    </a>
                  );
                }

                return (
                  <button key={idx} className={`${classes} cursor-default`}>
                    {cta.text} {isPrimary && <SafeIcon icon={FiArrowRight} className="h-4 w-4" />}
                  </button>
                );
              }) : (
                <>
                  <button className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#060b19] transition hover:bg-blue-50">
                    Visit JRob United <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                  </button>
                  <button className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10">
                    Learn More
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Decorative background glow that changes color based on brand */}
        <div className={`absolute -right-20 -top-20 h-64 w-64 rounded-full blur-[100px] opacity-20 transition-colors duration-700 ${
          activeTab % 2 === 0 ? 'bg-blue-500' : 'bg-purple-500'
        }`} />
      </div>
    </div>
  );
}